"use strict";
var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.User = void 0;
exports.isDriver = isDriver;
exports.isCompanyDriver = isCompanyDriver;
exports.isDriversCompany = isDriversCompany;
exports.isIndividualDriver = isIndividualDriver;
exports.isDriverOrCompanyActive = isDriverOrCompanyActive;
exports.isDriverFileOwner = isDriverFileOwner;
exports.isWillingToRentOneOfVehicleTypes = isWillingToRentOneOfVehicleTypes;
exports.getOwnerDriverId = getOwnerDriverId;
exports.getDriverTotalScore = getDriverTotalScore;
exports.isForceLoggedOut = isForceLoggedOut;
exports.getDriverTier = getDriverTier;
const constants_1 = require("@daytrip/constants");
const legacy_config_1 = require("@daytrip/legacy-config");
const legacy_enums_1 = require("@daytrip/legacy-enums");
const utils_1 = require("@daytrip/utils");
const class_transformer_1 = require("class-transformer");
const class_validator_1 = require("class-validator");
const type_graphql_1 = require("type-graphql");
const typeorm_1 = require("typeorm");
const uuid_1 = require("uuid");
const transformToDate_1 = require("../transformers/transformToDate");
const FullNestedValidator_1 = require("../validators/FullNestedValidator");
const BillingInformation_1 = require("./BillingInformation");
const Driver_1 = require("./Driver");
const DriversCompany_1 = require("./DriversCompany");
const DynamicFormFieldsValues_1 = require("./DynamicFormFieldsValues");
const HyperwalletInformation_1 = require("./HyperwalletInformation");
const MangopayInformation_1 = require("./MangopayInformation");
const ProfilePhoto_1 = require("./ProfilePhoto");
const StripeInformation_1 = require("./StripeInformation");
const TransporterInfo_1 = require("./TransporterInfo");
const TravelAgent_1 = require("./TravelAgent");
const UserAutomaticEmail_1 = require("./UserAutomaticEmail");
const UserRoles_1 = require("./UserRoles");
const Wishlist_1 = require("./Wishlist");
let User = class User {
    _id = (0, uuid_1.v4)();
    createdAt = new Date();
    deletedAt;
    birthdayAt;
    countryId = undefined;
    facebookId;
    appleUserId;
    appleUserToken;
    profilePhoto;
    profilePhotoUrl;
    email = "";
    otherEmails = [];
    paypalEmail;
    _passwordHash = `purposefullyInvalidInitialHash${Math.floor(Math.random() * 1000000000)}`;
    firstName = "";
    lastName = "";
    /**
     * The transformer checks the `countryId` of the instance. If the country is not the US,
     * it unsets the `regionId` of the nested `driver`.
     */
    driver;
    driversCompany;
    travelAgent;
    phoneNumber = "";
    whatsappNumber = "";
    payoutType = undefined; // defaulting to a *value* of undefined to help with mobx
    mangopayInformation = [];
    hyperwalletInformation;
    stripeInformation;
    billingInformation = [];
    dynamicFormFieldsValues = [];
    otherPhoneNumbers = [];
    userRoles;
    vehicleIds = [];
    automaticEmails = [];
    version = 0;
    lastLoginAt;
    distanceUnit = legacy_enums_1.DistanceUnit.Kilometers;
    discountCode;
    fcmRegistrationTokens = [];
    // set default based on order
    currency = legacy_enums_1.Currency.Euro;
    verifiedAt;
    wishlist = {
        routeIds: [],
        stopIds: [],
        cityIds: [],
    };
    customerNote;
    stripeCustomerId;
    /**
     This property is fully defined and used in the new API.
     It must be defined here as Column since it needs to be preserved in the database
     when manipulating with the User object in legacy.
     */
    publicOffersPreferences;
    optedOutOfMarketing = true;
    transporterInfo;
    googleSsoId;
    apiPartnerId;
    forceLoggedOutAt;
    constructor(init) {
        if (init) {
            Object.assign(this, init);
        }
    }
    isMangopayInformationSet() {
        return !!this.mangopayInformation && !!this.mangopayInformation[this.mangopayInformation.length - 1];
    }
    get activeMangopayInformation() {
        if (this.driver || this.driversCompany) {
            const billingInformation = this.billingInformation.find((bi) => bi.isActive);
            if (billingInformation) {
                return this.mangopayInformation.find((mi) => mi.billingInformationId === billingInformation._id);
            }
        }
        return this.mangopayInformation.find((mi) => (0, utils_1.isUndefinedOrNull)(mi.disabledAt));
    }
    get activeMangopayInformationIndex() {
        if (this.driver || this.driversCompany) {
            const billingInformation = this.billingInformation.find((bi) => bi.isActive);
            if (billingInformation) {
                return this.mangopayInformation.findIndex((mi) => mi.billingInformationId === billingInformation._id);
            }
        }
        return this.mangopayInformation.findIndex((mi) => (0, utils_1.isUndefinedOrNull)(mi.disabledAt));
    }
    getActiveMangopayInformationOrFail() {
        const info = this.activeMangopayInformation;
        if (!info) {
            throw Error(`No active mangopay information found on user ${this._id}`);
        }
        return info;
    }
    get invoicingBillingInformation() {
        return this.billingInformation.find((bi) => bi.isActive && !bi.deletedAt);
    }
    get fullName() {
        return [this.firstName, this.lastName].join(" ");
    }
    isKYCValid() {
        try {
            if (!this.isMangopayInformationSet()) {
                return false;
            }
            const active = this.getActiveMangopayInformationOrFail();
            return active.KYCLevel === legacy_enums_1.KYCLevel.Regular;
        }
        catch (e) {
            return false;
        }
    }
};
exports.User = User;
__decorate([
    (0, type_graphql_1.Field)(),
    (0, typeorm_1.ObjectIdColumn)(),
    (0, class_validator_1.IsUUID)("4", { always: true }),
    __metadata("design:type", String)
], User.prototype, "_id", void 0);
__decorate([
    (0, typeorm_1.Column)(),
    (0, class_validator_1.IsDate)({ always: true }),
    (0, class_validator_1.IsDefined)({ always: true }),
    transformToDate_1.TransformToDate,
    __metadata("design:type", Date)
], User.prototype, "createdAt", void 0);
__decorate([
    (0, typeorm_1.Column)(),
    (0, class_validator_1.IsOptional)({ always: true }),
    (0, class_validator_1.IsDate)({ always: true }),
    transformToDate_1.TransformToDate,
    __metadata("design:type", Date)
], User.prototype, "deletedAt", void 0);
__decorate([
    (0, type_graphql_1.Authorized)(),
    (0, type_graphql_1.Field)({ nullable: true }),
    (0, typeorm_1.Column)(),
    (0, class_validator_1.IsOptional)({ always: true }),
    (0, class_validator_1.IsDate)({ groups: [legacy_enums_1.UserValidationGroups.PROFILE] }),
    transformToDate_1.TransformToDate,
    __metadata("design:type", Date)
], User.prototype, "birthdayAt", void 0);
__decorate([
    (0, type_graphql_1.Field)({ nullable: true }),
    (0, typeorm_1.Column)(),
    (0, class_validator_1.IsUUID)("4", { groups: [legacy_enums_1.UserValidationGroups.PROFILE] }),
    (0, class_validator_1.IsOptional)({ always: true }),
    __metadata("design:type", String)
], User.prototype, "countryId", void 0);
__decorate([
    (0, type_graphql_1.Authorized)(),
    (0, type_graphql_1.Field)({ nullable: true }),
    (0, typeorm_1.Column)(),
    (0, class_validator_1.IsOptional)(),
    (0, class_validator_1.IsString)(),
    __metadata("design:type", String)
], User.prototype, "facebookId", void 0);
__decorate([
    (0, type_graphql_1.Authorized)(),
    (0, typeorm_1.Column)(),
    (0, class_validator_1.IsString)(),
    (0, class_validator_1.IsOptional)(),
    __metadata("design:type", String)
], User.prototype, "appleUserId", void 0);
__decorate([
    (0, type_graphql_1.Authorized)(),
    (0, typeorm_1.Column)(),
    (0, class_validator_1.IsString)(),
    (0, class_validator_1.IsOptional)(),
    __metadata("design:type", String)
], User.prototype, "appleUserToken", void 0);
__decorate([
    (0, typeorm_1.Column)(),
    (0, FullNestedValidator_1.ValidateNestedFull)(ProfilePhoto_1.ProfilePhoto, { groups: [legacy_enums_1.UserValidationGroups.PROFILE] }),
    (0, class_validator_1.IsOptional)({ groups: [legacy_enums_1.UserValidationGroups.PROFILE] }),
    (0, class_transformer_1.Type)(() => ProfilePhoto_1.ProfilePhoto),
    __metadata("design:type", ProfilePhoto_1.ProfilePhoto)
], User.prototype, "profilePhoto", void 0);
__decorate([
    (0, type_graphql_1.Field)({ nullable: true }),
    (0, typeorm_1.Column)(),
    (0, class_validator_1.IsOptional)({ groups: [legacy_enums_1.UserValidationGroups.PROFILE] }),
    (0, class_validator_1.IsUrl)(undefined, { groups: [legacy_enums_1.UserValidationGroups.PROFILE] }),
    __metadata("design:type", String)
], User.prototype, "profilePhotoUrl", void 0);
__decorate([
    (0, type_graphql_1.Authorized)(),
    (0, type_graphql_1.Field)(),
    (0, typeorm_1.Column)(),
    (0, class_validator_1.IsDefined)({ groups: [legacy_enums_1.UserValidationGroups.PROFILE] }),
    (0, class_validator_1.IsEmail)(undefined, { groups: [legacy_enums_1.UserValidationGroups.PROFILE] }),
    (0, class_validator_1.Length)(5, 48, { groups: [legacy_enums_1.UserValidationGroups.PROFILE] }),
    (0, class_validator_1.IsString)({ groups: [legacy_enums_1.UserValidationGroups.PROFILE] }),
    (0, class_validator_1.ValidateIf)((user) => !user.driver ||
        (user.driver && !user.driver.driversCompanyUserId) ||
        (user.driver && user.driver.driversCompanyUserId && user.email && user.email.length > 0), {
        groups: [legacy_enums_1.UserValidationGroups.PROFILE],
    }),
    (0, class_transformer_1.Transform)(({ value }) => value && value.trim().toLowerCase()),
    __metadata("design:type", String)
], User.prototype, "email", void 0);
__decorate([
    (0, typeorm_1.Column)(),
    (0, class_validator_1.IsArray)(),
    (0, class_validator_1.IsDefined)(),
    (0, class_transformer_1.Transform)(({ value }) => (value && Array.isArray(value) ? value.filter((item) => String(item) !== "") : [])),
    (0, class_transformer_1.Type)(() => String),
    __metadata("design:type", Array)
], User.prototype, "otherEmails", void 0);
__decorate([
    (0, typeorm_1.Column)(),
    (0, class_validator_1.IsString)(),
    (0, class_validator_1.IsOptional)(),
    __metadata("design:type", String)
], User.prototype, "paypalEmail", void 0);
__decorate([
    (0, typeorm_1.Column)(),
    (0, class_validator_1.IsString)(),
    (0, class_validator_1.IsDefined)(),
    __metadata("design:type", String)
], User.prototype, "_passwordHash", void 0);
__decorate([
    (0, type_graphql_1.Field)(),
    (0, typeorm_1.Column)(),
    (0, class_validator_1.Length)(0, 48, { groups: [legacy_enums_1.UserValidationGroups.PROFILE] }),
    (0, class_validator_1.IsString)({ groups: [legacy_enums_1.UserValidationGroups.PROFILE] }),
    (0, class_validator_1.IsNotEmpty)({ groups: [legacy_enums_1.UserValidationGroups.PROFILE] }),
    (0, class_validator_1.ValidateIf)((user) => !!user.driver || !!user.driversCompany || !!user.travelAgent, {
        groups: [legacy_enums_1.UserValidationGroups.PROFILE],
    }),
    __metadata("design:type", String)
], User.prototype, "firstName", void 0);
__decorate([
    (0, type_graphql_1.Field)(),
    (0, typeorm_1.Column)(),
    (0, class_validator_1.Length)(0, 48, { groups: [legacy_enums_1.UserValidationGroups.PROFILE] }),
    (0, class_validator_1.IsString)({ groups: [legacy_enums_1.UserValidationGroups.PROFILE] }),
    (0, class_validator_1.IsNotEmpty)({ groups: [legacy_enums_1.UserValidationGroups.PROFILE] }),
    (0, class_validator_1.ValidateIf)((user) => !!user.driver || !!user.driversCompany || !!user.travelAgent, {
        groups: [legacy_enums_1.UserValidationGroups.PROFILE],
    }),
    __metadata("design:type", String)
], User.prototype, "lastName", void 0);
__decorate([
    (0, typeorm_1.Column)(),
    (0, FullNestedValidator_1.ValidateNestedFull)(Driver_1.Driver, { groups: [legacy_enums_1.UserValidationGroups.PROFILE, legacy_enums_1.UserValidationGroups.DRIVER] }),
    (0, class_validator_1.IsOptional)({ groups: [legacy_enums_1.UserValidationGroups.PROFILE, legacy_enums_1.UserValidationGroups.DRIVER] }),
    (0, class_transformer_1.Transform)(({ value, obj }) => {
        if (value instanceof Driver_1.Driver && obj.countryId !== legacy_config_1.US_COUNTRY_ID) {
            value.regionId = undefined;
        }
        return value;
    }),
    (0, class_transformer_1.Type)(() => Driver_1.Driver),
    __metadata("design:type", Driver_1.Driver)
], User.prototype, "driver", void 0);
__decorate([
    (0, typeorm_1.Column)(),
    (0, FullNestedValidator_1.ValidateNestedFull)(DriversCompany_1.DriversCompany, {
        groups: [legacy_enums_1.UserValidationGroups.PROFILE, legacy_enums_1.UserValidationGroups.DRIVER_COMPANY],
    }),
    (0, class_validator_1.IsOptional)({
        groups: [legacy_enums_1.UserValidationGroups.PROFILE, legacy_enums_1.UserValidationGroups.DRIVER_COMPANY],
    }),
    (0, class_transformer_1.Type)(() => DriversCompany_1.DriversCompany),
    __metadata("design:type", DriversCompany_1.DriversCompany)
], User.prototype, "driversCompany", void 0);
__decorate([
    (0, type_graphql_1.Authorized)(),
    (0, type_graphql_1.Field)(() => TravelAgent_1.TravelAgent, { nullable: true }),
    (0, typeorm_1.Column)(),
    (0, FullNestedValidator_1.ValidateNestedFull)(TravelAgent_1.TravelAgent, { groups: [legacy_enums_1.UserValidationGroups.TRAVEL_AGENT] }),
    (0, class_validator_1.IsOptional)({ groups: [legacy_enums_1.UserValidationGroups.TRAVEL_AGENT] }),
    (0, class_transformer_1.Type)(() => TravelAgent_1.TravelAgent),
    __metadata("design:type", TravelAgent_1.TravelAgent)
], User.prototype, "travelAgent", void 0);
__decorate([
    (0, type_graphql_1.Authorized)(),
    (0, type_graphql_1.Field)(),
    (0, typeorm_1.Column)(),
    (0, class_validator_1.Length)(4, 18, { groups: [legacy_enums_1.UserValidationGroups.PROFILE] }),
    (0, class_validator_1.ValidateIf)((user) => !!user.driver, { groups: [legacy_enums_1.UserValidationGroups.PROFILE] }),
    (0, class_validator_1.IsString)({ groups: [legacy_enums_1.UserValidationGroups.PROFILE] }),
    (0, class_validator_1.IsDefined)({ groups: [legacy_enums_1.UserValidationGroups.PROFILE] }),
    (0, class_transformer_1.Transform)(({ value }) => (0, utils_1.transformPhoneNumberToNormalizedForm)(value)),
    __metadata("design:type", String)
], User.prototype, "phoneNumber", void 0);
__decorate([
    (0, typeorm_1.Column)(),
    (0, class_validator_1.ValidateIf)((user) => !!user.driver, { groups: [legacy_enums_1.UserValidationGroups.PROFILE] }),
    (0, class_validator_1.Length)(0, 18, { groups: [legacy_enums_1.UserValidationGroups.PROFILE] }),
    (0, class_validator_1.IsString)({ groups: [legacy_enums_1.UserValidationGroups.PROFILE] }),
    (0, class_validator_1.IsDefined)({ groups: [legacy_enums_1.UserValidationGroups.PROFILE] }),
    (0, class_transformer_1.Transform)(({ value }) => (0, utils_1.transformPhoneNumberToNormalizedForm)(value)),
    __metadata("design:type", String)
], User.prototype, "whatsappNumber", void 0);
__decorate([
    (0, typeorm_1.Column)(),
    (0, class_validator_1.IsEnum)(legacy_enums_1.PayoutType, {
        groups: [legacy_enums_1.UserValidationGroups.BILLING, legacy_enums_1.UserValidationGroups.DRIVER, legacy_enums_1.UserValidationGroups.DRIVER_COMPANY],
        message: "invalid payout type",
    }),
    (0, class_validator_1.IsOptional)({
        groups: [legacy_enums_1.UserValidationGroups.BILLING, legacy_enums_1.UserValidationGroups.DRIVER, legacy_enums_1.UserValidationGroups.DRIVER_COMPANY],
    }),
    __metadata("design:type", Number)
], User.prototype, "payoutType", void 0);
__decorate([
    (0, type_graphql_1.Authorized)(),
    (0, type_graphql_1.Field)(() => [MangopayInformation_1.MangopayInformation]),
    (0, typeorm_1.Column)(),
    (0, FullNestedValidator_1.ValidateNestedFull)(MangopayInformation_1.MangopayInformation, { groups: [legacy_enums_1.UserValidationGroups.BILLING] }),
    (0, class_transformer_1.Transform)(({ value }) => (!value ? [] : (0, class_transformer_1.plainToClass)(MangopayInformation_1.MangopayInformation, value))),
    (0, class_transformer_1.Type)(() => MangopayInformation_1.MangopayInformation),
    __metadata("design:type", Array)
], User.prototype, "mangopayInformation", void 0);
__decorate([
    (0, typeorm_1.Column)(),
    (0, FullNestedValidator_1.ValidateNestedFull)(HyperwalletInformation_1.HyperwalletInformation, { groups: [legacy_enums_1.UserValidationGroups.HYPERWALLET_INFORMATION] }),
    (0, class_validator_1.IsOptional)({ groups: [legacy_enums_1.UserValidationGroups.HYPERWALLET_INFORMATION] }),
    (0, class_transformer_1.Type)(() => HyperwalletInformation_1.HyperwalletInformation),
    __metadata("design:type", HyperwalletInformation_1.HyperwalletInformation)
], User.prototype, "hyperwalletInformation", void 0);
__decorate([
    (0, typeorm_1.Column)(),
    (0, FullNestedValidator_1.ValidateNestedFull)(StripeInformation_1.StripeInformation, { groups: [legacy_enums_1.UserValidationGroups.STRIPE_INFORMATION] }),
    (0, class_validator_1.ValidateIf)((_user, stripeInformation) => !!stripeInformation, {
        groups: [legacy_enums_1.UserValidationGroups.STRIPE_INFORMATION],
    }),
    (0, class_transformer_1.Type)(() => StripeInformation_1.StripeInformation),
    __metadata("design:type", StripeInformation_1.StripeInformation)
], User.prototype, "stripeInformation", void 0);
__decorate([
    (0, typeorm_1.Column)(),
    (0, FullNestedValidator_1.ValidateNestedFull)(BillingInformation_1.BillingInformation, { groups: [legacy_enums_1.UserValidationGroups.BILLING] }),
    (0, class_transformer_1.Transform)(({ value }) => (!value ? [] : (0, class_transformer_1.plainToClass)(BillingInformation_1.BillingInformation, value))),
    (0, class_transformer_1.Type)(() => BillingInformation_1.BillingInformation),
    __metadata("design:type", Array)
], User.prototype, "billingInformation", void 0);
__decorate([
    (0, typeorm_1.Column)()
    // don't validate nested, it's validated separately when created and doesn't matter later
    ,
    (0, class_transformer_1.Transform)(({ value }) => (!value ? [] : (0, class_transformer_1.plainToClass)(DynamicFormFieldsValues_1.DynamicFormFieldsValues, value))),
    (0, class_transformer_1.Type)(() => DynamicFormFieldsValues_1.DynamicFormFieldsValues),
    __metadata("design:type", Array)
], User.prototype, "dynamicFormFieldsValues", void 0);
__decorate([
    (0, typeorm_1.Column)(),
    (0, class_validator_1.IsArray)({ groups: [legacy_enums_1.UserValidationGroups.PROFILE] }),
    (0, class_validator_1.IsDefined)({ groups: [legacy_enums_1.UserValidationGroups.PROFILE] }),
    (0, class_transformer_1.Type)(() => String),
    (0, class_transformer_1.Transform)(({ value }) => value && Array.isArray(value) ? value.map(utils_1.transformPhoneNumberToNormalizedForm).filter((item) => String(item) !== "") : []),
    __metadata("design:type", Array)
], User.prototype, "otherPhoneNumbers", void 0);
__decorate([
    (0, typeorm_1.Column)(),
    (0, FullNestedValidator_1.ValidateNestedFull)(UserRoles_1.UserRoles, { groups: [legacy_enums_1.UserValidationGroups.PROFILE] }),
    (0, class_transformer_1.Type)(() => UserRoles_1.UserRoles),
    (0, class_validator_1.IsOptional)({ always: true }),
    __metadata("design:type", UserRoles_1.UserRoles)
], User.prototype, "userRoles", void 0);
__decorate([
    (0, typeorm_1.Column)(),
    (0, class_validator_1.IsArray)(),
    (0, class_validator_1.IsDefined)(),
    (0, class_transformer_1.Type)(() => String),
    __metadata("design:type", Array)
], User.prototype, "vehicleIds", void 0);
__decorate([
    (0, typeorm_1.Column)(),
    (0, class_validator_1.IsArray)(),
    (0, class_validator_1.IsDefined)(),
    (0, class_validator_1.ValidateNested)(),
    (0, class_transformer_1.Type)(() => UserAutomaticEmail_1.UserAutomaticEmail),
    __metadata("design:type", Array)
], User.prototype, "automaticEmails", void 0);
__decorate([
    (0, typeorm_1.Column)(),
    __metadata("design:type", Number)
], User.prototype, "version", void 0);
__decorate([
    (0, typeorm_1.Column)(),
    (0, class_validator_1.IsDate)(),
    (0, class_validator_1.IsOptional)(),
    transformToDate_1.TransformToDate,
    __metadata("design:type", Date)
], User.prototype, "lastLoginAt", void 0);
__decorate([
    (0, type_graphql_1.Field)(() => legacy_enums_1.DistanceUnit),
    (0, typeorm_1.Column)(),
    (0, class_validator_1.IsInt)(),
    (0, class_validator_1.IsDefined)(),
    __metadata("design:type", Number)
], User.prototype, "distanceUnit", void 0);
__decorate([
    (0, typeorm_1.Column)(),
    (0, class_validator_1.IsString)(),
    (0, class_validator_1.IsOptional)(),
    __metadata("design:type", String)
], User.prototype, "discountCode", void 0);
__decorate([
    (0, typeorm_1.Column)(),
    (0, class_validator_1.IsOptional)(),
    (0, class_validator_1.IsArray)(),
    __metadata("design:type", Array)
], User.prototype, "fcmRegistrationTokens", void 0);
__decorate([
    (0, type_graphql_1.Field)(() => legacy_enums_1.Currency),
    (0, typeorm_1.Column)(),
    (0, class_validator_1.IsNumber)(),
    (0, class_validator_1.IsDefined)(),
    __metadata("design:type", Number)
], User.prototype, "currency", void 0);
__decorate([
    (0, type_graphql_1.Authorized)(),
    (0, type_graphql_1.Field)(),
    (0, typeorm_1.Column)(),
    (0, class_validator_1.IsDate)(),
    (0, class_validator_1.IsOptional)(),
    transformToDate_1.TransformToDate,
    __metadata("design:type", Date)
], User.prototype, "verifiedAt", void 0);
__decorate([
    (0, type_graphql_1.Authorized)(),
    (0, type_graphql_1.Field)(() => Wishlist_1.Wishlist),
    (0, typeorm_1.Column)(),
    __metadata("design:type", Object)
], User.prototype, "wishlist", void 0);
__decorate([
    (0, type_graphql_1.Authorized)(),
    (0, type_graphql_1.Field)(),
    (0, typeorm_1.Column)(),
    (0, class_validator_1.IsOptional)(),
    __metadata("design:type", String)
], User.prototype, "customerNote", void 0);
__decorate([
    (0, type_graphql_1.Authorized)(),
    (0, type_graphql_1.Field)({ nullable: true }),
    (0, typeorm_1.Column)(),
    (0, class_validator_1.IsOptional)(),
    __metadata("design:type", String)
], User.prototype, "stripeCustomerId", void 0);
__decorate([
    (0, typeorm_1.Column)(),
    __metadata("design:type", Object)
], User.prototype, "publicOffersPreferences", void 0);
__decorate([
    (0, type_graphql_1.Authorized)(),
    (0, type_graphql_1.Field)(),
    (0, typeorm_1.Column)(),
    (0, class_validator_1.IsBoolean)(),
    __metadata("design:type", Boolean)
], User.prototype, "optedOutOfMarketing", void 0);
__decorate([
    (0, class_validator_1.IsOptional)(),
    (0, type_graphql_1.Authorized)(),
    (0, typeorm_1.Column)(),
    __metadata("design:type", TransporterInfo_1.TransporterInfo)
], User.prototype, "transporterInfo", void 0);
__decorate([
    (0, class_validator_1.IsOptional)(),
    (0, typeorm_1.Column)(),
    __metadata("design:type", String)
], User.prototype, "googleSsoId", void 0);
__decorate([
    (0, class_validator_1.IsOptional)(),
    (0, typeorm_1.Column)(),
    __metadata("design:type", String)
], User.prototype, "apiPartnerId", void 0);
__decorate([
    (0, class_validator_1.IsOptional)({ always: true }),
    (0, typeorm_1.Column)(),
    (0, class_validator_1.IsDate)({ groups: [legacy_enums_1.UserValidationGroups.DRIVER, legacy_enums_1.UserValidationGroups.DRIVER_COMPANY] }),
    transformToDate_1.TransformToDate,
    __metadata("design:type", Date)
], User.prototype, "forceLoggedOutAt", void 0);
exports.User = User = __decorate([
    (0, typeorm_1.Entity)("users"),
    (0, type_graphql_1.ObjectType)("User"),
    __metadata("design:paramtypes", [Object])
], User);
function isDriver(user) {
    return !!user.driver;
}
function isCompanyDriver(user) {
    return !!user.driver?.driversCompanyUserId;
}
function isDriversCompany(user) {
    return !!user.driversCompany;
}
function isIndividualDriver(user) {
    return isDriver(user) && !isCompanyDriver(user);
}
function isDriverOrCompanyActive(user) {
    return user.driver?.status === legacy_enums_1.DriverStatus.Active || user.driversCompany?.status === legacy_enums_1.DriversCompanyStatus.Active;
}
function isDriverFileOwner(user, fileId) {
    const driverDocuments = user.driver ? user.driver.documents : user.driversCompany?.documents || [];
    const biDocuments = user.billingInformation?.reduce((acc, bi) => {
        return acc.concat(bi.documents || []);
    }, []) || [];
    const allDriverDocumentsFileIds = [...driverDocuments, ...biDocuments].flatMap((doc) => doc.fileIds);
    return allDriverDocumentsFileIds.includes(fileId);
}
function isWillingToRentOneOfVehicleTypes(user, vehicleTypes) {
    let userVehicleTypes;
    if (isDriver(user)) {
        userVehicleTypes = user.driver.willingToRentVehicleTypes;
    }
    else if (isDriversCompany(user)) {
        userVehicleTypes = user.driversCompany.willingToRentVehicleTypes;
    }
    else {
        throw new Error("User is not a driver or a drivers company");
    }
    return userVehicleTypes.some((vehicleType) => vehicleTypes.includes(vehicleType));
}
function getOwnerDriverId(driver) {
    return driver.driver?.driversCompanyUserId || driver._id;
}
function getDriverTotalScore(driver) {
    const driverFields = driver.driver ?? driver.driversCompany;
    if (!driverFields) {
        return constants_1.INITIAL_DRIVER_SCORE_RATING;
    }
    return driverFields.score.totalScore;
}
/**
 * Check if a driver user's token should be force logged out based on JWT iat and user's forceLoggedOutAt
 * @param user User object
 * @param tokenIssuedAt JWT issued at timestamp (in seconds)
 * @returns true if token should be rejected due to force logout
 */
function isForceLoggedOut(user, tokenIssuedAt) {
    // only apply to driver users (for now?)
    if (!isDriver(user) && !isCompanyDriver(user) && !isDriversCompany(user)) {
        return false;
    }
    if (!user.forceLoggedOutAt || !tokenIssuedAt) {
        return false;
    }
    const tokenIssuedAtMilliseconds = tokenIssuedAt * 1000;
    const forceLogoutMilliseconds = user.forceLoggedOutAt.getTime();
    return tokenIssuedAtMilliseconds < forceLogoutMilliseconds;
}
function getDriverTier(driver) {
    const driverFields = driver.driver ?? driver.driversCompany;
    if (!driverFields) {
        return;
    }
    return driverFields.score.tier;
}
//# sourceMappingURL=User.js.map